<template>
  <div class="comments-item d-flex column-gap-3" :class="{'comments-item--fading': isFading}" data-locator="comment-item">
    <app-avatar class="comment-avatar no-shrink" :initials="item.user.initials" />
    <div class="w-100">
      <div class="d-flex column-gap-2 text--x-small">
        <div class="text--bold" v-html="displayUserFullName"></div>
        <span class="text-grey-600">{{ displayDate }}</span>
      </div>
      <div class="w-100 mt-1" v-html="displayMessage"></div>
    </div>
    <AppMenu :menu="menuItems" v-if="menuItems.length">
      <template #activator="{ props }">
          <app-button type="icon" data-locator="other-actions-menu-button" v-bind="props">
          <app-icon-ds
              icon="ellipsis-h"
              weight="far"
              size="xs"
          />
          </app-button>
      </template>
    </AppMenu>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import AppMenu from '@/common/AppMenu.vue'
import { ISOToShortDate } from '@/common/utils/dates'

const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPaneCommentsItem'

export default {
  name: 'ChecklistPaneCommentsItem',
  components: { AppMenu },
  emits: ['delete-comment'],
  props: {
    isFading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  methods: {
    deleteComment() {
      this.$emit('delete-comment', this.item.id)
    },
    highlightText(string, substring) {
      if (!substring) return string

      const regexRule = new RegExp(`(${substring})`, 'gi')
      const parser = new DOMParser()
      const doc = parser.parseFromString(string, 'text/html')

      const highlightNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const tempDiv = document.createElement('div')
          
          tempDiv.innerHTML = node.nodeValue.replace(regexRule, '<span class="text--highlight">$1</span>')

          while (tempDiv.firstChild) {
            node.parentNode.insertBefore(tempDiv.firstChild, node)
          }
          
          node.parentNode.removeChild(node)
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          node.childNodes.forEach(highlightNode)
        }
      };

      doc.body.childNodes.forEach(highlightNode)
      return doc.body.innerHTML
    },
  },
  computed: {
    ...mapState('user', ['profile']),
    canDeleteComment() {
      return this.item.user.userId === this.profile.id
    },
    displayDate() {
      return ISOToShortDate(this.item.createdAt)
    },
    displayMessage() {
      if(!this.hasSearchQuery) return this.item.message
      return this.highlightText(this.item.message, this.searchQuery)
    },
    displayUserFullName() {
      if(!this.hasSearchQuery) return this.item.user.fullName
      return this.highlightText(this.item.user.fullName, this.searchQuery)
    },
    hasSearchQuery() {
      return this.searchQuery.length > 1
    },
    menuItems() {
      const menuItems = []
      if(this.canDeleteComment) {
        menuItems.push({
          icon: 'trash-alt',
          id: 'delete-comment',
          style: 'text-error',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.menuItems.deleteComment`),
          action: () => this.deleteComment(),
        })
      }

      return menuItems
    },
  },
}
</script>

<style lang="scss" scoped>
.comments-item {
  &--fading {
    opacity: 0.5;
  }

  .comment-avatar {
    background-color: rgb(var(--v-theme-grey-lighten-5));
    border: 1px solid rgb(var(--v-theme-grey-lighten-4));
    color: rgb(var(--v-theme-grey-600));
  }
}
:deep(.text--highlight) {
  background-color: rgb(var(--v-theme-dark-blue-lighten-4));
}
:deep(span[data-mention]) {
  color: rgb(var(--v-theme-info-darken-1));
  display: inline-block;

  &:before {
    content: '@';
  }
}
</style>